import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, X, GraduationCap, User, LogOut } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import copy from '../copy';

const Header: React.FC = () => {
  const { getItemCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { openAuthModal } = useModal();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-primary-100 sticky top-0 z-50">
      <div className="container-max px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-accent-600 to-tech-600 rounded-xl flex items-center justify-center shadow-lg">
              <GraduationCap className="text-white w-6 h-6" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-primary-900">IberoTech</span>
              <span className="text-xs text-primary-500 -mt-1">Technology Foundation</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-primary-700 hover:text-accent-600 transition-colors font-medium">
              {copy.nav_inicio}
            </Link>
            <Link to="/cursos" className="text-primary-700 hover:text-accent-600 transition-colors font-medium">
              {copy.nav_cursos}
            </Link>
          </nav>

          {/* Cart, Auth and Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            <Link to="/carrito" className="relative p-3 text-primary-700 hover:text-accent-600 transition-colors bg-primary-50 rounded-lg hover:bg-primary-100">
              <ShoppingCart className="w-5 h-5" />
              {getItemCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  {getItemCount()}
                </span>
              )}
            </Link>
            
            {/* Auth Section */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 text-primary-700 hover:text-accent-600 transition-colors bg-primary-50 rounded-lg hover:bg-primary-100"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:block text-sm font-medium">
                    {user?.firstName}
                  </span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                      <div className="font-medium">{user?.firstName} {user?.lastName}</div>
                      <div className="text-gray-500">{user?.email}</div>
                    </div>
                    <Link
                      to="/dashboard"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <User className="w-4 h-4" />
                      <span>Mi cuenta</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{copy.auth.login.logout}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => openAuthModal()}
                className="flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors font-medium"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:block">Iniciar sesión</span>
              </button>
            )}
            
            <button
              className="md:hidden p-2 text-primary-700 hover:text-accent-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-primary-100 py-6 bg-white/95 backdrop-blur-sm">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="text-primary-700 hover:text-accent-600 transition-colors font-medium py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {copy.nav_inicio}
              </Link>
              <Link 
                to="/cursos" 
                className="text-primary-700 hover:text-accent-600 transition-colors font-medium py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {copy.nav_cursos}
              </Link>
              {!isAuthenticated && (
                <button
                  onClick={() => {
                    openAuthModal();
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-primary-700 hover:text-accent-600 transition-colors font-medium py-2"
                >
                  Iniciar sesión
                </button>
              )}
              {isAuthenticated && (
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left text-primary-700 hover:text-accent-600 transition-colors font-medium py-2"
                >
                  {copy.auth.login.logout}
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 